// Pure logic of the shared DateField (core/date-field.tsx): splitting an ISO day into day / month / year segments,
// reading what the advisor typed, validating impossible dates and leap years, and min / max bounds.
// The value contract is the one every date field had with <input type="date">: an ISO 'YYYY-MM-DD' string or ''.
// Nothing here touches the DOM, so it is unit-tested directly (tests/unit/date-field.test.ts).

export type Segment = 'day' | 'month' | 'year';
/** What the three segments hold while the advisor types (digits only, possibly incomplete) */
export interface DateDraft { day: string; month: string; year: string }
export type DateIssue = 'incomplete' | 'day' | 'month' | 'year' | 'no_such_day' | 'feb29' | 'before_min' | 'after_max';
export interface DateCheck {
  /** the value to commit: a full valid ISO day, or '' (empty field, or a draft that is not a valid date) */
  value: string;
  issue: DateIssue | null;
}
/** Which dates a field accepts. `min` / `max` are ISO days; the year range is what the year segment steps through. */
export interface DateBounds { min: string | null; max: string | null; yearMin: number; yearMax: number }

export const EMPTY_DRAFT: DateDraft = { day: '', month: '', year: '' };
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const pad2 = (n: number) => String(n).padStart(2, '0');

export function isLeapYear(y: number): boolean { return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0; }
export function daysInMonth(y: number, m: number): number {
  return m === 2 ? (isLeapYear(y) ? 29 : 28) : [4, 6, 9, 11].includes(m) ? 30 : 31;
}

/** ISO day → segments ('' or anything that is not an ISO day → empty segments) */
export function splitIso(iso: string | null | undefined): DateDraft {
  const m = ISO.exec(String(iso ?? '').trim());
  return m ? { year: m[1]!, month: m[2]!, day: m[3]! } : { ...EMPTY_DRAFT };
}

/** segments → ISO day, or null when they are not a complete real date (no range check) */
export function buildIso(d: DateDraft): string | null {
  if (!/^\d{1,2}$/.test(d.day) || !/^\d{1,2}$/.test(d.month) || !/^\d{4}$/.test(d.year)) return null;
  const day = +d.day, month = +d.month, year = +d.year;
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return `${d.year}-${pad2(month)}-${pad2(day)}`;
}

/** The bounds of a field. `birth` = 1900-01-01 … today; otherwise 1900 … 2100 unless min / max say more. */
export function dateBounds(o: { kind?: 'birth' | 'any'; min?: string | null; max?: string | null; today: string }): DateBounds {
  const valid = (x: string | null | undefined) => (x && buildIso(splitIso(x)) ? x : null);
  let min = valid(o.min), max = valid(o.max);
  if (o.kind === 'birth') { min = min && min > '1900-01-01' ? min : '1900-01-01'; max = max && max < o.today ? max : valid(o.today); }
  const yearMin = min ? +min.slice(0, 4) : Math.min(1900, max ? +max.slice(0, 4) : 1900);
  const yearMax = max ? +max.slice(0, 4) : Math.max(2100, yearMin);
  return { min, max, yearMin, yearMax };
}

/** Validate the draft. Empty → '' with no issue; anything else that is not a full valid date in range → '' + issue. */
export function checkDraft(d: DateDraft, b: DateBounds): DateCheck {
  const day = d.day.trim(), month = d.month.trim(), year = d.year.trim();
  if (!day && !month && !year) return { value: '', issue: null };
  if (day && day !== '0' && (!/^\d{1,2}$/.test(day) || +day < 1 || +day > 31)) return { value: '', issue: 'day' };
  if (month && month !== '0' && (!/^\d{1,2}$/.test(month) || +month < 1 || +month > 12)) return { value: '', issue: 'month' };
  if (!day || !month || day === '0' || month === '0' || !/^\d{4}$/.test(year)) return { value: '', issue: 'incomplete' };
  const y = +year;
  if (y < b.yearMin || y > b.yearMax) return { value: '', issue: 'year' };
  if (+day > daysInMonth(y, +month)) return { value: '', issue: +month === 2 && +day === 29 ? 'feb29' : 'no_such_day' };
  const iso = buildIso({ day, month, year })!;
  if (b.min && iso < b.min) return { value: '', issue: 'before_min' };
  if (b.max && iso > b.max) return { value: '', issue: 'after_max' };
  return { value: iso, issue: null };
}

/** The locale whose numeric order the DateField uses: the app's users are in Cyprus, so English is day/month/year
 *  (en-GB) like Greek and Russian. Only the field's segment order — dates the app prints keep the i18n locale. */
export function dateFieldLocale(locale: string): string { return /^en(-|$)/i.test(locale) ? 'en-GB' : locale; }

/** What the field hands its parent after a change: a new valid day, '' when the advisor cleared it, or null = nothing.
 *  `last` = what the parent holds now; `base` = the value when this round of editing began. A half-typed or impossible
 *  draft never emits '' or a partial: the parent goes back to / keeps `base` (typing «31042000» over 15/06/2000 passes
 *  through the valid 03/06/2000 — that must not stay saved), so Save never changes or erases a stored date by accident
 *  while the field shows its message. */
export function emission(c: DateCheck, last: string, base: string = last): string | null {
  if (c.issue) return last !== base ? base : null;
  return c.value !== last ? c.value : null;
}

/** Segment order of a locale, from Intl (el → day month year; en follows the app's i18n locale like every date it prints) */
export function segmentOrder(locale: string): Segment[] {
  try {
    const parts = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(new Date(Date.UTC(2000, 5, 15, 12)));
    const order = parts.map((p) => p.type).filter((t): t is Segment => t === 'day' || t === 'month' || t === 'year');
    if (order.length === 3 && new Set(order).size === 3) return order;
  } catch { /* unknown locale */ }
  return ['day', 'month', 'year'];
}
/** The separator a locale prints between the numeric parts ('/' '.' '-') */
export function segmentSeparator(locale: string): string {
  try {
    const lit = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(new Date(Date.UTC(2000, 5, 15, 12))).find((p) => p.type === 'literal')?.value.trim();
    if (lit) return lit;
  } catch { /* unknown locale */ }
  return '/';
}

/**
 * A whole date typed or pasted into one segment: '15/06/2000', '15.6.2000', '15-06-2000' (in the locale order),
 * ISO '2000-06-15', or 8 digits '15062000' (locale order). Returns null when the text is not a whole date.
 */
export function parseTyped(text: string, order: Segment[]): DateDraft | null {
  const s = String(text ?? '').trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) return { year: m[1]!, month: pad2(+m[2]!), day: pad2(+m[3]!) };
  const out: DateDraft = { ...EMPTY_DRAFT };
  m = /^(\d{1,4})\s*[/.\-\s]\s*(\d{1,4})\s*[/.\-\s]\s*(\d{1,4})$/.exec(s);
  if (m) {
    order.forEach((seg, i) => { out[seg] = m![i + 1]!; });
    if (!/^\d{4}$/.test(out.year) || out.day.length > 2 || out.month.length > 2) return null;
    return { year: out.year, month: pad2(+out.month), day: pad2(+out.day) };
  }
  if (/^\d{8}$/.test(s)) {
    let i = 0;
    for (const seg of order) { const n = seg === 'year' ? 4 : 2; out[seg] = s.slice(i, i + n); i += n; }
    return out;
  }
  return null;
}

/** Keep only digits, at most the segment's length */
export function cleanSegment(seg: Segment, raw: string): string { return raw.replace(/\D/g, '').slice(0, seg === 'year' ? 4 : 2); }

/** After typing into a segment: is it complete, so focus moves on? (day "4"… already means 04; month "2"… means 02) */
export function segmentDone(seg: Segment, v: string): boolean {
  if (seg === 'year') return v.length === 4;
  if (v.length === 2) return true;
  return v.length === 1 && (seg === 'day' ? +v > 3 : +v > 1);
}

/** ↑ / ↓ / PgUp / PgDn on a segment: step within its range, wrapping day and month; year clamps to the field's range */
export function stepSegment(d: DateDraft, seg: Segment, delta: number, b: DateBounds, today: string): DateDraft {
  const t = splitIso(today);
  if (seg === 'year') {
    const cur = /^\d{4}$/.test(d.year) ? +d.year : null;
    const start = cur ?? Math.min(Math.max(+t.year || 2000, b.yearMin), b.yearMax);
    const y = cur === null ? start : Math.min(Math.max(start + delta, b.yearMin), b.yearMax);
    return { ...d, year: String(y) };
  }
  const max = seg === 'day' ? 31 : 12;
  const cur = /^\d{1,2}$/.test(d[seg]) ? +d[seg] : null;
  const n = cur === null ? (+t[seg] || 1) : ((((cur - 1 + delta) % max) + max) % max) + 1;
  return { ...d, [seg]: pad2(n) };
}
